import { onMounted, onUnmounted } from 'vue';
import {
    useTranslation, useSnackbar, useWebSocket,
} from '@/composables';
import type { useAntennaMonitoringStore } from '@/stores/antennaMonitoringStore';
import type { AntennaActiveReadingType, WebSocketConfigType } from '@/types/WebSocketType';
import type { UseAntennaWebSocketReturnType } from '@/types/composables/WebSocketComposableTypes';
import { formatDate } from '@/utils/dateUtils';
import { useAuthStore } from '@/stores/authStore';
import { useCompanyStore } from '@superadmin/stores/companyStore';

export type UseAntennaWebSocketOptionsType = {
    autoConnect?: boolean;
};

export function useAntennaWebSocket(
    store: ReturnType<typeof useAntennaMonitoringStore>,
    options: UseAntennaWebSocketOptionsType = {},
): UseAntennaWebSocketReturnType {
    const { autoConnect = true } = options;
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const authStore = useAuthStore();
    const webSocket = useWebSocket({ url: 'http://localhost:3001' });

    const fetchCompanyDataForSuperAdmin = async () => {
        if (authStore.user?.is_super_admin) {
            const companyStore = useCompanyStore();

            if (companyStore.items.length === 0) {
                await companyStore.fetchAll({ showAllItems: true, sortBy: 'name', sortDirection: 'asc' });
                store.selectedTenantId =
                    companyStore.items.length > 0 && !store.selectedTenantId ? companyStore.items[0].tenant_id : null;
            }
        }
    };

    const getWebSocketConfig = async (): Promise<WebSocketConfigType | null> => {
        const baseUrl = authStore.user?.environment?.connector_url;

        if (!baseUrl) {
            console.error('No WebSocket URL available from user environment');
            showSnackbar(t('errors.noWebSocketUrl'));

            return null;
        }

        const tenantId = store.currentTenantId;
        if (tenantId === null) {
            console.error('No tenant data available for WebSocket connection');
            showSnackbar(t('errors.noTenantSelected'));

            return null;
        }

        return {
            url: `${baseUrl}/tenant-${tenantId}`,
            token: authStore?.authToken?.access_token || '',
        };
    };

    const connect = async () => {
        try {
            await fetchCompanyDataForSuperAdmin();
            await reconnectForTenant();
            store.updateConnectionStatus(webSocket.connectionStatus, webSocket.connectionId);
        } catch (error) {
            console.error('WebSocket connection failed:', error);
        }
    };

    const disconnect = () => {
        cleanupEventListeners();
        webSocket.disconnect();
    };

    const setupEventListeners = (): void => {
        for (const antenna of store.antennas) {
            const eventName = `antenna-active-readings-${antenna.uuid}`;
            webSocket.on(eventName, (data: AntennaActiveReadingType) => {
                handleAntennaReadings(data, antenna.uuid);
            });
        }
    };

    const handleAntennaReadings = (data: AntennaActiveReadingType, antennaUuid?: string) => {
        const readings = data.readings;

        store.addReading({
            antennaUuid: antennaUuid ?? data.antennaUuid,
            readings,
            timestamp: formatDate(new Date(), t('dateFormats.datetime')),
        });
    };

    const requestAllAntennaReadings = (): void => {
        for (const antenna of store.antennas) {
            getAntennaActiveReadings(antenna.uuid);
        }
    };

    const getAntennaActiveReadings = (antennaUuid: string) => {
        webSocket.emit('get-antenna-active-readings', { antennaUuid });
    };

    const cleanupEventListeners = () => {
        for (const antenna of store.antennas) {
            const eventName = `antenna-active-readings-${antenna.uuid}`;
            webSocket.off(eventName);
        }
    };

    const reconnectForTenant = async () => {
        cleanupEventListeners();
        store.readings = [];
        const newConfig = await getWebSocketConfig();
        if (!newConfig) {
            console.error('[WebSocket] Cannot reconnect: Invalid tenant configuration');

            return;
        }
        await webSocket.reconnectWithNewConfig(newConfig);
        await store.fetchAntennas();
        setupEventListeners();
        requestAllAntennaReadings();
    };

    const sendTestData = (antennaUuid: string) => {
        const generateRandomEPCs = () => {
            const count = Math.floor(Math.random() * 21);
            const epcs = [];
            for (let i = 0; i < count; i++) {
                epcs.push(generateRandomString(8));
            }

            return epcs;
        };

        const generateRandomString = (length: number) => {
            const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
            let str = '';
            for (let i = 0; i < length; i++) {
                str += chars[Math.floor(Math.random() * chars.length)];
            }

            return str;
        };

        const epcIds = generateRandomEPCs();
        webSocket.emit('set-antenna-active-readings', {
            epcIds: epcIds,
            antennaUuid,
        });
    };

    onMounted(() => {
        if (autoConnect) {
            connect();
        }
    });

    onUnmounted(() => {
        disconnect();
    });

    return {
        isConnected: webSocket.isConnected,
        connectionId: webSocket.connectionId,
        connectionError: webSocket.connectionError,
        isConnecting: webSocket.isConnecting,
        connectionStatus: webSocket.connectionStatus,
        reconnectForTenant,
        connect,
        disconnect,
        sendTestData,
        fetchCompanyDataForSuperAdmin,
    };
}
